import { unref } from 'vue'
import CyPluginWidgetIframe from '@/components/CyPluginWidgetIframe.vue'
import CyPluginWidgetTable from '@/components/CyPluginWidgetTable.vue'
import { getRouter } from '@/router/holder'
import { useAuthStore } from '@/store/pinia/auth'
import { useOrganizationStore } from '@/store/pinia/organization'
import {
  adaptPluginWidgetToPlacement,
  getPlacements,
  registerPlacement,
  registerPlacements,
  unregisterPlacement,
  registerWidgetType,
} from '@/utils/placements'
import { ydAPI } from '@/utils/plugins/vue-cycloid-plugin'

const RUNTIME_PLACEMENT_SOURCE = 'plugin-widget-api'

const getRouteContext = (route) => {
  const params = route?.params || {}
  let orgCanonical = _.toString(params.orgCanonical || '')
  const projectCanonical = _.toString(params.projectCanonical || '')
  const envCanonical = _.toString(params.envCanonical || '')
  const componentCanonical = _.toString(params.componentCanonical || '')

  if (!orgCanonical) {
    orgCanonical = useOrganizationStore().orgCanonical || ''
  }

  if (!orgCanonical) return null

  const isComponentContext = Boolean(projectCanonical && envCanonical && componentCanonical)

  return {
    orgCanonical,
    projectCanonical: projectCanonical || null,
    envCanonical: envCanonical || null,
    componentCanonical: componentCanonical || null,
    isComponentContext,
    key: isComponentContext
      ? `component:${orgCanonical}/${projectCanonical}/${envCanonical}/${componentCanonical}`
      : `org:${orgCanonical}`,
  }
}

const getRuntimePlacements = () => {
  return getPlacements().filter((placement) => _.get(placement, 'meta.source') === RUNTIME_PLACEMENT_SOURCE)
}

const removeRuntimePlacements = (placementsApi) => {
  getRuntimePlacements().forEach(({ id }) => placementsApi.unregister({ id }))
}

const syncRuntimePlacements = (placementsApi, nextPlacements) => {
  // Remove all existing runtime placements first to prevent race conditions
  // where multiple sync calls could register duplicates
  const previous = getRuntimePlacements()

  // Build a Set of next IDs for efficient checking
  const nextIds = new Set(nextPlacements.map(({ id }) => id))

  // Remove ALL existing runtime placements - we'll add back what we need
  previous.forEach(({ id }) => {
    placementsApi.unregister({ id })
  })

  // Also remove any placements that match the IDs we're about to register,
  // even if they weren't caught by getRuntimePlacements() due to missing/wrong meta.source
  // This is a defensive measure to prevent duplicates
  const allCurrentPlacements = getPlacements()
  allCurrentPlacements.forEach((placement) => {
    if (nextIds.has(placement.id) && placement.id.startsWith('plugin-widget-')) {
      placementsApi.unregister({ id: placement.id })
    }
  })

  // Now register all new placements with replace: true as an extra safety measure
  placementsApi.registerMany(nextPlacements, { replace: true })
}

const listOrgWidgets = async (context) => {
  const response = await ydAPI.listPluginWidgets(context.orgCanonical, 'sideMenuPage')
  return _.isArray(response?.data) ? response.data : []
}

const listComponentWidgets = async (context) => {
  if (!context.isComponentContext) return []
  const response = await ydAPI.listComponentPluginWidgets(
    context.orgCanonical,
    context.projectCanonical,
    context.envCanonical,
    context.componentCanonical,
  )
  return _.isArray(response?.data) ? response.data : []
}

const fetchWidgetQueryData = async ({ scope, context, widget }) => {
  if (_.get(widget, 'type') !== 'table') return null

  try {
    if (scope === 'component') {
      const response = await ydAPI.getComponentPluginWidgetData(
        context.orgCanonical,
        context.projectCanonical,
        context.envCanonical,
        context.componentCanonical,
        widget.id,
      )
      return response?.data ?? null
    }

    const response = await ydAPI.getPluginWidgetData(
      context.orgCanonical,
      widget.id,
    )
    return response?.data ?? null
  } catch (error) {
    console.warn(`[placements] Failed to fetch query data for widget "${widget?.id}"`)
    return null
  }
}

const buildPlacementsFromWidgets = async ({ context, orgWidgets, componentWidgets }) => {
  const toPlacement = async (widget, scope) => {
    const queryData = await fetchWidgetQueryData({ scope, context, widget })
    return adaptPluginWidgetToPlacement({ widget, scope, context, queryData })
  }

  const allPlacements = await Promise.all([
    ...orgWidgets.map((widget) => toPlacement(widget, 'org')),
    ...componentWidgets.map((widget) => toPlacement(widget, 'component')),
  ])

  return allPlacements.filter(Boolean)
}

const createRuntimeWidgetsSync = (placementsApi) => {
  const syncedContexts = new Map()
  let lastOrgCanonical = null
  let lastPermissions = null
  let syncInProgress = false
  let pendingContext = null
  let hasPendingContext = false
  let latestRequestedKey = null

  const syncContext = async (context) => {
    latestRequestedKey = context?.key ?? null
    // Serialize sync operations to avoid duplicate registrations, but keep the
    // latest requested context instead of dropping it: if a sync is already
    // running, remember the most recent context and let the in-flight sync
    // re-run with it once it finishes, so the current route/org always wins.
    if (syncInProgress) {
      pendingContext = context
      hasPendingContext = true
      return
    }

    syncInProgress = true
    try {
      let nextContext = context
      do {
        hasPendingContext = false
        await performSync(nextContext)
        nextContext = pendingContext
        pendingContext = null
      } while (hasPendingContext)
    } finally {
      syncInProgress = false
      pendingContext = null
      hasPendingContext = false
    }
  }

  const performSync = async (context) => {
    if (!context) {
      syncedContexts.clear()
      lastOrgCanonical = null
      lastPermissions = null
      removeRuntimePlacements(placementsApi)
      return
    }

    const permissions = useAuthStore().permissions ?? null
    const orgChanged = lastOrgCanonical !== null && lastOrgCanonical !== context.orgCanonical
    const permissionsChanged = lastPermissions !== null && lastPermissions !== permissions
    if (orgChanged || permissionsChanged) {
      syncedContexts.clear()
    }
    lastOrgCanonical = context.orgCanonical
    lastPermissions = permissions

    const orgKey = `org:${context.orgCanonical}`
    const componentKey = context.isComponentContext ? context.key : null

    if (!syncedContexts.has(orgKey)) {
      try {
        const orgWidgets = await listOrgWidgets(context)
        const placements = await buildPlacementsFromWidgets({ context, orgWidgets, componentWidgets: [] })
        syncedContexts.set(orgKey, placements)
      } catch {
        console.warn('[placements] Failed to sync org plugin widgets')
        syncedContexts.set(orgKey, [])
      }
    }

    if (componentKey && !syncedContexts.has(componentKey)) {
      try {
        const componentWidgets = await listComponentWidgets(context)
        const placements = await buildPlacementsFromWidgets({ context, orgWidgets: [], componentWidgets })
        syncedContexts.set(componentKey, placements)
      } catch {
        console.warn('[placements] Failed to sync component plugin widgets')
        syncedContexts.set(componentKey, [])
      }
    }

    // Register only if this is still the most recently requested context. Comparing
    // against router.currentRoute here would break navigations: guards run before the
    // route is confirmed, so currentRoute still points at the previous org and every
    // org-switch navigation would silently drop the placements it just fetched,
    // leaving the previous org's items in the sidebar (ENG-217).
    if (context.key !== latestRequestedKey) return

    const orgPlacements = syncedContexts.get(orgKey) ?? []
    const componentPlacements = componentKey ? (syncedContexts.get(componentKey) ?? []) : []
    syncRuntimePlacements(placementsApi, [...orgPlacements, ...componentPlacements])
  }

  getRouter().beforeEach(async (to) => {
    await syncContext(getRouteContext(to))
  })

  return {
    refresh () {
      syncedContexts.clear()
      // An org switch swaps the token before (or without) a route change, so the route
      // can still name the previous org, whose widgets the new token is then denied: a
      // guaranteed 403 per refresh (ENG-359). Sync the org the token is scoped to; the
      // route only contributes its component context when it agrees with the token.
      const route = unref(getRouter().currentRoute)
      const orgCanonical = useAuthStore().jwtDecoded?.organization?.canonical || route?.params?.orgCanonical
      const isRouteInSync = route?.params?.orgCanonical === orgCanonical
      return syncContext(getRouteContext(isRouteInSync ? route : { params: { orgCanonical } }))
    },
  }
}

const createPlacementsApi = () => ({
  register (placement, options = {}) {
    return registerPlacement(placement, options)
  },
  registerMany (placements, options = {}) {
    return registerPlacements(placements, options)
  },
  unregister ({ id } = {}) {
    return unregisterPlacement({ id })
  },
})

/**
 * Fetches and registers an org's side-menu plugin widgets outside the runtime's
 * route-driven sync. The runtime only applies placements for the current route's
 * context, so the dashboard-denied redirect guard, which decides for a target
 * org, must populate the registry itself before routing to pluginWidget
 * (ensurePluginWidgetExists 404s on unregistered widgets).
 *
 * @param {string} orgCanonical
 * @returns {Promise<boolean>} resolves `false` when nothing was synced so callers can exclude plugin sections
 */
export const syncOrgPlacements = async (orgCanonical) => {
  if (!orgCanonical) return false
  const context = {
    orgCanonical,
    projectCanonical: null,
    envCanonical: null,
    componentCanonical: null,
    isComponentContext: false,
    key: `org:${orgCanonical}`,
  }
  try {
    const orgWidgets = await listOrgWidgets(context)
    const placements = await buildPlacementsFromWidgets({ context, orgWidgets, componentWidgets: [] })
    syncRuntimePlacements(createPlacementsApi(), placements)
    return true
  } catch {
    console.warn('[placements] Failed to sync org plugin widgets for the dashboard-denied redirect')
    return false
  }
}

export default {
  install (app) {
    registerWidgetType('iframe', CyPluginWidgetIframe)
    registerWidgetType('table', CyPluginWidgetTable)

    const placementsApi = createPlacementsApi()
    const runtimeWidgetsSync = createRuntimeWidgetsSync(placementsApi)
    placementsApi.refresh = runtimeWidgetsSync.refresh

    app.config.globalProperties.$placements = placementsApi
  },
}
